import React, { useEffect, useMemo, useRef, useState } from 'react';
import LivePreviewStrip from './LivePreviewStrip';
import { taglineFor } from '../utils/exploreStart';

/**
 * SpaceSwipeDeck — the fresh-phone-visitor's first taste of /explore (Nina 08-14).
 *
 * 🃏 09-29 (Nina's /explore batch): it no longer opens by itself on a first
 * visit. A phone opens it with "Discover" in /explore's sticky bar, as often
 * as it likes; the page deals it from the room list's own rooms
 * (utils/exploreStart discoverDeal), skipping rooms this device already loved.
 * "Once ever" below is history.
 *
 * THREE spaces, each from a DIFFERENT category, one card at a time. It is an
 * attention grab first and a data source second (Nina's framing), so this file
 * optimises for feel: full-bleed art, a card that tracks the thumb, a stamp
 * that fades in as you commit.
 *
 * 🚪 The deck NEVER enters a space (Nina 08-28, superseding the original
 * "exit lands you inside a space" goal): no card tap (08-14), no Peek button,
 * no end-card Enter. Like/pass only; every exit lands on the grid, whose
 * Space of the Week banner leads with the liveliest room.
 *
 * Deliberate design calls (see the /explore ledger for the full reasoning):
 *   · RIGHT = a real ❤️ like (the existing rateSpace mechanic, via the page's
 *     handleLike). LEFT = a private pass — it does NOT write dislikes_count.
 *     A bored thumb must never permanently mark a space down.
 *   · The three cards are tiered strong / mid / under-exposed and then SHUFFLED.
 *     Pure random risks a newcomer's very first impression being the weakest
 *     space on the platform; always-strong-first buries everyone else forever.
 *   · Card position is passed to onImpression because position bias is baked in
 *     from day one and cannot be reconstructed later.
 *   · Swipe rate judges the CARD (art, name, blurb), not the space. Nothing
 *     here should ever be read as space quality on its own — the honest signal
 *     is what happens after entry.
 *   · Mobile only, once ever, always skippable. /explore is the front door, and
 *     a front door you cannot walk past is a wall.
 *
 * 🎬 SHOW, DON'T TELL (Nina 08-15). The first pass told you what the deck was
 * ("THREE ROOMS. WHICH ONES ARE YOU?") over a still photo, which is a caption
 * where a room should be. It now borrows the live rail card's anatomy instead,
 * because that card already answers "what is this place" without a sentence:
 *   · the MOD's face on a ring, hanging off the art — a room has a host;
 *   · that host's STATUS in a speech bubble ("tail twitching fast") — the host
 *     is doing something right now;
 *   · GHOST CHATTER drifting over the art — the room is mid-conversation.
 * The counter that replaced the headline is a bare `1 / 3`: it says how far in
 * you are, and nothing else, because nothing else needed saying.
 *
 * ⚡ Cost of the chatter (PERF_TODO model): transform/opacity keyframes on ≤5
 * bubbles + 5 emoji = compositor work. It is mounted on the TOP card only and
 * paused mid-drag, so a thumb that is actually moving the card competes with
 * nothing. reduce-motion turns it off outright.
 */

const SWIPE_THRESHOLD = 82;   // px of drag that commits
const FLING_MS = 300;
// TAP_SLOP retired 08-14 with card-tap-to-enter (see onPointerUp): a tap on the
// card is now a no-op, so there is nothing left to measure tap distance for.

const buzz = (ms) => { try { navigator.vibrate?.(ms); } catch (e) { /* unsupported */ } };

// 🔒 09-29: the chatter's quiet-room fallback draws "@name" bubbles from the
// room's seats, and a seat can be a person (a visitor's handle, or on a ported
// room its owner's). The deck hands it the room's AIs only. (`is_human` also
// marks connected agents: they drop out too, which only costs a fallback name.)
const aiOnly = (space) => ({ ...space, players: (space.players || []).filter(p => p && !p.is_human) });

const SpaceSwipeDeck = ({
    cards = [],            // [{ space, categoryLabel, tier }]
    onLike = () => {},
    // onEnter retired 08-28 (Nina): the deck never enters a space anymore —
    // no card tap (08-14), no Peek button, no end-card CTA. Like/pass only.
    onBrowse = () => {},
    onImpression = () => {},
}) => {
    const [idx, setIdx] = useState(0);
    const [drag, setDrag] = useState(null);
    const [fling, setFling] = useState(null);
    const [liked, setLiked] = useState([]);
    const startRef = useRef(null);
    const flingTimerRef = useRef(null);
    const loggedRef = useRef(new Set());
    // One copy per card, not per drag frame (the strip memoizes on it).
    const previews = useMemo(() => new Map(cards.map(c => [c.space.id, aiOnly(c.space)])), [cards]);

    const reduceMotion = useMemo(() => {
        try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
        catch (e) { return false; }
    }, []);

    // The page behind must not scroll under a thumb that misses the card.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);
    useEffect(() => () => clearTimeout(flingTimerRef.current), []);

    const current = cards[idx] || null;
    const next = cards[idx + 1] || null;
    const done = cards.length > 0 && idx >= cards.length;

    // One impression per card, at the moment it becomes the top card.
    useEffect(() => {
        if (!current) return;
        const key = current.space.id;
        if (loggedRef.current.has(key)) return;
        loggedRef.current.add(key);
        onImpression({ space: current.space, position: idx, category: current.categoryLabel, tier: current.tier });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [current, idx]);

    const commit = (dir) => {
        if (!current || fling) return;
        buzz(dir > 0 ? 18 : 10);
        const card = current;
        const finish = () => {
            if (dir > 0) {
                setLiked(l => [...l, card]);
                onLike(card.space);
            }
            setIdx(i => i + 1);
            setFling(null);
            setDrag(null);
        };
        if (reduceMotion) { finish(); return; }
        setFling({ dir });
        flingTimerRef.current = setTimeout(finish, FLING_MS);
    };

    const onPointerDown = (e) => {
        if (fling) return;
        startRef.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!startRef.current || fling) return;
        setDrag({ dx: e.clientX - startRef.current.x, dy: e.clientY - startRef.current.y });
    };
    const onPointerUp = () => {
        if (!startRef.current || fling) return;
        const d = drag || { dx: 0, dy: 0 };
        startRef.current = null;
        if (Math.abs(d.dx) >= SWIPE_THRESHOLD) { commit(d.dx > 0 ? 1 : -1); return; }
        // 👆 A tap on the CARD deliberately does nothing (Nina 08-14 eve).
        // It used to call onEnter and navigate straight into the space, which
        // made every half-committed swipe, every scroll-that-wasn't, and every
        // "let me look at this one" a one-way trip out of the deck — on the
        // exact surface (a phone) where an imprecise touch is the norm.
        // 08-28 (Nina): the deck no longer enters spaces AT ALL — "Peek
        // inside" and the end-card Enter CTA are both gone. Like/pass here,
        // then the grid (Space of the Week banner first) does the landing.
        setDrag(null); // spring back
    };

    const dxNow = drag?.dx || 0;
    const dx = fling ? fling.dir * (window.innerWidth + 260) : dxNow;
    const dy = fling ? -46 : (drag?.dy || 0) * 0.22;
    const rot = fling ? fling.dir * 20 : dxNow * 0.055;
    const stamp = Math.min(1, Math.abs(dxNow) / SWIPE_THRESHOLD);
    const dragging = !!drag && !fling;
    // The next card rises toward full size as the top one leaves — the depth
    // cue that makes a stack feel like a stack.
    const rise = Math.min(1, Math.abs(dxNow) / (SWIPE_THRESHOLD * 1.6));

    // 🎙️ Who fronts this room. Same rule as the live rail card: the moderator
    // if the space has one (mods are the face of a room), else the first AI
    // player with a portrait. `status_messages` is the mod's own playful status
    // list — measured 08-15: all 50 live spaces ship a moderator, most with
    // several statuses, so this is a populated field and not a hopeful one.
    const hostOf = (s) => {
        if (s.moderator?.profile_image_url || s.moderator?.name) {
            return {
                name: s.moderator.name,
                avatarUrl: s.moderator.profile_image_url,
                emoji: s.moderator.avatar_emoji || null,
                statuses: Array.isArray(s.moderator.status_messages) ? s.moderator.status_messages : [],
            };
        }
        const ai = (s.players || []).find(p => !p.is_human && p.profile_image_url);
        return ai ? { name: ai.name, avatarUrl: ai.profile_image_url, emoji: null, statuses: [] } : null;
    };

    const renderCard = (card, isTop) => {
        const s = card.space;
        const host = hostOf(s);
        // One status per card, chosen by a stable hash of the space id rather
        // than at random: the deck is built once per mount, and a status that
        // re-rolled on every drag frame would read as a glitch.
        const status = host && host.statuses.length
            ? host.statuses[String(s.id).split('').reduce((a, c) => a + c.charCodeAt(0), 0) % host.statuses.length]
            : null;
        return (
            <div
                key={s.id}
                data-deck-card={s.id}
                onPointerDown={isTop ? onPointerDown : undefined}
                onPointerMove={isTop ? onPointerMove : undefined}
                onPointerUp={isTop ? onPointerUp : undefined}
                onPointerCancel={isTop ? onPointerUp : undefined}
                style={{
                    ...styles.card,
                    ...(isTop ? {
                        transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg)`,
                        transition: dragging ? 'none' : `transform ${fling ? FLING_MS : 260}ms cubic-bezier(0.22,1,0.36,1)`,
                        touchAction: 'none', zIndex: 2,
                    } : {
                        transform: `scale(${(0.94 + 0.06 * rise).toFixed(3)}) translateY(${(12 - 12 * rise).toFixed(1)}px)`,
                        transition: dragging ? 'none' : 'transform 260ms ease',
                        zIndex: 1, opacity: 0.96,
                    }),
                }}
            >
                <img src={s.banner_image_url} alt="" draggable={false} style={styles.art} />

                {/* 💬 The room, mid-conversation. TOP CARD ONLY and frozen
                    while the thumb is moving — the card itself is being
                    transformed every frame, and a second animation underneath
                    it buys nothing a moving card can show. */}
                {isTop && !reduceMotion && (
                    <LivePreviewStrip
                        space={previews.get(s.id) || aiOnly(s)}
                        accentColor="#c4b5fd"
                        animate={!dragging && !fling}
                        fallDistance={620}
                        riseDistance={420}
                        // BACKGROUND, not foreground (Nina's word). At the
                        // rail's 1.0 these pills dominate a card three times
                        // the rail strip's height — they read as the content
                        // rather than as a room talking behind it.
                        bubbleScale={0.86}
                    />
                )}

                <div style={styles.scrim} />

                {card.categoryLabel && (
                    <div style={styles.categoryChip}>{card.categoryLabel}</div>
                )}

                {(s.is_live || s.viewer_count > 0) && (
                    <div style={styles.liveBadge}>
                        <span style={styles.liveBadgeDot} />
                        LIVE
                    </div>
                )}

                {isTop && (
                    <>
                        <div style={{ ...styles.stamp, ...styles.stampYes, opacity: dxNow > 0 ? stamp : (fling?.dir === 1 ? 1 : 0) }}>
                            ❤️ LOVE IT
                        </div>
                        <div style={{ ...styles.stamp, ...styles.stampNo, opacity: dxNow < 0 ? stamp : (fling?.dir === -1 ? 1 : 0) }}>
                            NEXT
                        </div>
                    </>
                )}

                {/* 🎙️ The host, hanging off the art onto the copy — the live
                    rail card's signature move. The face lands ABOVE the name so
                    a room reads as "someone's place" before it reads as a
                    title, and the status bubble beside it is the show-not-tell
                    beat: nobody has to be told the space is alive. */}
                {host && (
                    <div style={styles.hostRow}>
                        <div style={styles.hostRing}>
                            {host.avatarUrl ? (
                                <img src={host.avatarUrl} alt="" draggable={false} style={styles.hostImg} />
                            ) : (
                                <div style={styles.hostFallback}>{host.emoji || '✨'}</div>
                            )}
                        </div>
                        <div style={{ minWidth: 0 }}>
                            {host.name && <div style={styles.hostName}>@{host.name}</div>}
                            {status && <div style={styles.hostStatus}>{status}</div>}
                        </div>
                    </div>
                )}

                <div style={styles.cardBody}>
                    <div style={styles.cardName}>{s.name}</div>
                    {(taglineFor(s) || s.description) && (
                        <div style={styles.cardBlurb}>{taglineFor(s) || s.description}</div>
                    )}
                    <div style={styles.cardMeta}>
                        {(s.viewer_count > 0) && <span>👀 {s.viewer_count} here now</span>}
                        {(s.likes_count > 0) && <span>❤️ {s.likes_count}</span>}
                        {/* No entry affordance on the card at all (08-28): the
                            deck is like/pass only — see the actionRow note. */}
                        <span style={styles.tapHint}>swipe to sort</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.overlay}>
            <style>{`
                @keyframes deckPulse { 0%,100% { opacity: 0.55 } 50% { opacity: 1 } }
                @keyframes deckRise { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: none } }
            `}</style>

            {/* ✕ LEFT, same reason as the marketplace deck (Nina 08-14): the
                LofiRadio music FAB is global chrome fixed top-right at zIndex
                100010 and paints over this overlay, so nothing of ours may
                live in that corner. /explore mounts LofiRadio too. */}
            <div style={styles.topBar}>
                <button onClick={onBrowse} aria-label="Skip and browse all spaces" style={styles.closeX}>✕</button>
                {/* 1 / 3 — Nina 08-15, replacing both the "THREE ROOMS. WHICH
                    ONES ARE YOU?" headline AND the progress dots. The headline
                    explained the deck; the dots then explained it a second
                    time. A counter says the one thing neither the art nor the
                    card can (how far in you are) and says nothing else. */}
                {!done && current && (
                    <div style={styles.counter} aria-label={`Space ${idx + 1} of ${cards.length}`}>
                        <span style={styles.counterNow}>{idx + 1}</span>
                        <span style={styles.counterOf}> / {cards.length}</span>
                    </div>
                )}
            </div>

            {!done && current && (
                <>
                    <div style={styles.stack}>
                        {next && renderCard(next, false)}
                        {renderCard(current, true)}
                    </div>
                    {/* 🚪 No "Peek inside" either (Nina 08-28, same ruling as
                        the end-card CTA): the deck never enters a space. It is
                        a taste-collector — like/pass only — and every exit
                        lands on the grid, where the Space of the Week banner
                        leads with the liveliest room. deck-log 'enter' rows
                        can no longer occur. */}
                    <div style={styles.actionRow}>
                        <button onClick={() => commit(-1)} aria-label="Next space" style={{ ...styles.actionBtn, ...styles.passBtn }}>✕</button>
                        <button onClick={() => commit(1)} aria-label="Love it" style={{ ...styles.actionBtn, ...styles.likeBtn }}>❤️</button>
                    </div>
                    <div style={styles.hint}>Swipe right if it is your vibe. Left for the next one.</div>
                </>
            )}

            {done && (
                <div style={styles.endCard}>
                    <div style={styles.endEmoji}>{liked.length ? '✨' : '👋'}</div>
                    <div style={styles.endTitle}>
                        {liked.length ? 'Good taste.' : 'Plenty more inside.'}
                    </div>
                    <div style={styles.endSub}>
                        {/* 09-29: Discover can deal fewer than three (a device that loved most rooms) */}
                        {cards.length === 3 ? 'That was three of fifty. The rest are waiting.' : 'The rest are waiting.'}
                    </div>
                    {/* 🚪 No direct-enter CTA here (Nina 08-28): the deck used
                        to end on "Enter <liked[0]>", which gambled the
                        once-ever first impression on ONE room's liveness at
                        that moment. Every exit now lands on the grid, whose
                        Space of the Week banner leads with the liveliest room.
                        (Peek inside went in the same ruling — see actionRow.) */}
                    <button onClick={onBrowse} style={styles.endPrimary}>
                        Browse all spaces
                    </button>
                </div>
            )}
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 5200,
        background: 'radial-gradient(circle at 50% 0%, #241a3d 0%, #120c1f 55%, #08060e 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        overflow: 'hidden', color: '#fff',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    topBar: {
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        // 56px right inset = the music FAB's reserved lane (mobile FAB is 34px
        // at right:10 → occupies 349-383 on a 393pt phone).
        padding: '12px 56px 4px 14px',
        // ⭐⭐ LOAD-BEARING (08-15). This app has NO global box-sizing reset —
        // index.css and App.css both lack one — so `width: 100%` + 70px of
        // padding made this bar 463px wide inside a 393px viewport, and
        // anything `marginLeft: auto` put at its right edge landed ~70px
        // OFF-SCREEN, under the FAB. That is why the progress dots this
        // counter replaced were never visible on a phone: they were rendering
        // the whole time, just past the right edge. Measured, not guessed.
        boxSizing: 'border-box',
    },
    counter: {
        marginLeft: 'auto', display: 'flex', alignItems: 'baseline',
        fontVariantNumeric: 'tabular-nums', letterSpacing: '0.04em',
        // ⚠️ Both load-bearing (caught by the phone-viewport probe, 08-15):
        // without them the flex row squeezed the counter to the width of one
        // glyph, "/ 3" wrapped onto a second line, and the wrapped half landed
        // under the global music FAB. It read as a bare "1".
        whiteSpace: 'nowrap', flexShrink: 0,
    },
    counterNow: { fontSize: '1.05rem', fontWeight: 800, color: '#fff' },
    counterOf: { fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255,255,255,0.45)' },
    closeX: {
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.75)',
        border: '1px solid rgba(255,255,255,0.18)',
        fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'inherit',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    stack: {
        position: 'relative', flex: 1, width: 'min(420px, calc(100vw - 26px))',
        // The retired headline gave its vertical space back to the art.
        minHeight: 0, margin: '6px auto 0',
    },
    card: {
        position: 'absolute', inset: '0 0 6px', borderRadius: 20, overflow: 'hidden',
        background: '#17122a', border: '1px solid rgba(196,181,253,0.22)',
        boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
        userSelect: 'none', WebkitUserSelect: 'none',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        cursor: 'grab',
    },
    art: {
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'cover', pointerEvents: 'none',
    },
    scrim: {
        position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 2,
        background: 'linear-gradient(to top, rgba(8,6,14,0.95) 0%, rgba(8,6,14,0.72) 26%, rgba(8,6,14,0.12) 58%, rgba(8,6,14,0.35) 100%)',
    },
    categoryChip: {
        position: 'absolute', top: 14, left: 14, zIndex: 3,
        fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: '#efe7ff',
        background: 'rgba(20,12,40,0.72)', border: '1px solid rgba(196,181,253,0.4)',
        borderRadius: 999, padding: '5px 11px', backdropFilter: 'blur(6px)',
    },
    liveBadge: {
        // ⚠️ LEFT, under the category chip — NOT the card's top-right corner.
        // /explore's "+ BUILD" page CTA is fixed chrome that paints OVER this
        // overlay (as does the ☀ toggle and the music FAB — see the topBar
        // note), and it lands exactly on the card's top-right. The live rail
        // card puts LIVE top-left for its own reasons; this agrees with it.
        position: 'absolute', top: 46, left: 14, zIndex: 3,
        display: 'flex', alignItems: 'center', gap: 5,
        fontSize: '0.6rem', fontWeight: 900, letterSpacing: '0.1em',
        color: '#fff', background: 'rgba(235,4,0,0.92)',
        borderRadius: 999, padding: '4px 10px',
    },
    liveBadgeDot: {
        width: 6, height: 6, borderRadius: '50%', background: '#fff',
        // Opacity only — an animated box-shadow here would be a paint loop over
        // the moving card (PERF_TODO cost model).
        animation: 'deckPulse 2s ease-in-out infinite',
    },
    // 🎙️ Host block — sits between the art and the copy, inside the scrim, so
    // the face reads against dark rather than against whatever the banner is.
    hostRow: {
        position: 'relative', zIndex: 3,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '0 18px 8px',
    },
    hostRing: {
        width: 46, height: 46, borderRadius: '50%', flexShrink: 0,
        overflow: 'hidden', background: '#17122a',
        border: '2px solid rgba(196,181,253,0.75)',
        boxShadow: '0 4px 14px rgba(0,0,0,0.55)',
    },
    hostImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    hostFallback: {
        width: '100%', height: '100%', display: 'flex',
        alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem',
        background: 'linear-gradient(135deg, #7C4DFF, #5c16c5)',
    },
    hostName: {
        fontSize: '0.72rem', fontWeight: 700, color: 'rgba(255,255,255,0.72)',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        textShadow: '0 1px 6px rgba(0,0,0,0.8)',
    },
    hostStatus: {
        fontSize: '0.8rem', fontStyle: 'italic', lineHeight: 1.3,
        color: '#e9e0ff', marginTop: 1,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        textShadow: '0 1px 8px rgba(0,0,0,0.9)',
    },
    stamp: {
        position: 'absolute', top: 60, zIndex: 4, fontSize: '1.3rem', fontWeight: 900,
        letterSpacing: '0.05em', padding: '6px 14px', borderRadius: 10,
        border: '3px solid', pointerEvents: 'none', transition: 'opacity 0.1s linear',
    },
    stampYes: { left: 16, color: '#7ee2a8', borderColor: '#7ee2a8', transform: 'rotate(-13deg)', background: 'rgba(6,22,13,0.6)' },
    stampNo: { right: 16, color: '#ff8a8a', borderColor: '#ff8a8a', transform: 'rotate(13deg)', background: 'rgba(26,8,8,0.6)' },
    cardBody: { position: 'relative', zIndex: 3, padding: '0 18px 20px', display: 'flex', flexDirection: 'column', gap: 7 },
    cardName: { fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.15, textShadow: '0 2px 12px rgba(0,0,0,0.8)' },
    cardBlurb: {
        fontSize: '0.88rem', lineHeight: 1.4, color: 'rgba(255,255,255,0.86)',
        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        textShadow: '0 1px 8px rgba(0,0,0,0.8)',
    },
    cardMeta: {
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)',
    },
    tapHint: { marginLeft: 'auto', opacity: 0.55, fontStyle: 'italic' },
    actionRow: {
        display: 'flex', gap: 16, justifyContent: 'center', alignItems: 'center',
        padding: '14px 0 4px', flexShrink: 0,
    },
    actionBtn: {
        width: 56, height: 56, borderRadius: '50%', fontSize: '1.25rem', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit',
        background: 'rgba(255,255,255,0.05)', transition: 'transform 0.15s ease',
    },
    passBtn: { border: '2px solid rgba(255,138,138,0.6)', color: '#ff8a8a' },
    likeBtn: { border: '2px solid rgba(126,226,168,0.6)', color: '#7ee2a8' },
    hint: {
        textAlign: 'center', fontSize: '0.72rem', color: 'rgba(255,255,255,0.42)',
        padding: '0 16px calc(12px + env(safe-area-inset-bottom, 0px))',
    },
    endCard: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 10, padding: '0 28px calc(26px + env(safe-area-inset-bottom, 0px))', textAlign: 'center',
        animation: 'deckRise 0.35s ease-out',
    },
    endEmoji: { fontSize: 52 },
    endTitle: { fontSize: '1.45rem', fontWeight: 800 },
    endSub: { fontSize: '0.9rem', color: 'rgba(255,255,255,0.62)', marginBottom: 12, lineHeight: 1.45 },
    endPrimary: {
        background: 'linear-gradient(135deg, #7C4DFF, #a970ff)', color: '#fff', border: 'none',
        borderRadius: 14, padding: '14px 26px', fontSize: '1rem', fontWeight: 800,
        cursor: 'pointer', fontFamily: 'inherit', width: 'min(320px, 100%)',
        boxShadow: '0 8px 26px rgba(124,77,255,0.4)',
    },
};

export default SpaceSwipeDeck;
